/**
 * Connections (owner and managers): the KAF WhatsApp number, Google, and who can be
 * reached on what (docs/BRIEF-CONNECTIONS.md).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, LogOut, Mail, MessageCircle, RefreshCw, Search, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import ConfirmDialog from '@/components/ui/ConfirmDialog';
import { Pill } from '@/components/connect/MyChannelsCard';
import { cn } from '@/lib/cn';
import { errText, fetchReachability, fetchWhatsAppConnection, whatsAppAction, fetchGoogle, saveGoogleClient, googleStartUrl, googleDisconnect, googleTest, type ReachRow } from '@/lib/connections';

const PHASE: Record<string, { label: string; tone: string; hint: string }> = {
  connected: { label: 'Connected', tone: 'text-emerald-300', hint: 'Messages go out from this number.' },
  qr_required: { label: 'Scan to connect', tone: 'text-amber-300', hint: 'Scan the code with the KAF phone.' },
  qr_pending: { label: 'Scan to connect', tone: 'text-amber-300', hint: 'Scan the code with the KAF phone.' },
  initializing: { label: 'Starting', tone: 'text-amber-300', hint: 'A few seconds.' },
  connecting: { label: 'Connecting', tone: 'text-amber-300', hint: 'A few seconds.' },
  disconnected: { label: 'Disconnected', tone: 'text-red-300', hint: 'Reconnect, or scan again.' },
  logged_out: { label: 'Logged out', tone: 'text-red-300', hint: 'Start again and scan with the KAF phone.' },
  not_started: { label: 'Not started', tone: 'text-muted', hint: 'Start it, then scan with the KAF phone.' },
  not_configured: { label: 'Not set up', tone: 'text-muted', hint: 'The WhatsApp bridge is not configured on this server yet.' },
  bridge_unreachable: { label: 'Bridge not answering', tone: 'text-red-300', hint: 'The WhatsApp bridge on the server is down. Messages wait in the queue until it is back.' },
};

function WhatsAppCard() {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['wa-connection'], queryFn: fetchWhatsAppConnection,
    refetchInterval: (s) => (s.state.data?.status === 'connected' ? 30_000 : 3_000),
  });
  const act = useMutation({
    mutationFn: whatsAppAction,
    onSuccess: (d) => qc.setQueryData(['wa-connection'], d),
    onError: (e) => toast.error(errText(e, 'Could not do that')),
  });
  const [confirmLogout, setConfirmLogout] = useState(false);
  const d = q.data;
  const phase = PHASE[d?.status ?? ''] ?? { label: d?.status ?? '...', tone: 'text-muted', hint: '' };
  const pct = d && d.daily_cap ? Math.min(100, Math.round((d.sent_today / d.daily_cap) * 100)) : 0;

  return (
    <Card className="mb-4" data-testid="wa-connection">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-emerald-500/15 text-emerald-300 flex items-center justify-center shrink-0"><MessageCircle size={20} /></div>
        <div className="flex-1">
          <h2 className="font-medium text-muted-strong">WhatsApp</h2>
          <p className="text-xs text-muted">The dedicated KAF PRO number. Everything the team and the doctors get on WhatsApp comes from here.</p>
        </div>
        {d?.dry_run && <span className="text-[11px] rounded-full border border-amber-500/40 text-amber-300 px-2 py-0.5">test mode: nothing is sent</span>}
      </div>
      {!d && <div className="text-sm text-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Checking...</div>}
      {d && (
        <div className="grid gap-4 md:grid-cols-[1fr_auto]">
          <div className="space-y-3">
            <div>
              <div className={cn('text-lg font-medium', phase.tone)} data-testid="wa-phase">{phase.label}</div>
              <div className="text-xs text-muted">{phase.hint}</div>
            </div>
            {d.number && (
              <div className="flex items-center gap-2 text-sm text-muted-strong"><Smartphone size={14} className="text-muted" /> +{d.number}{d.name ? ` · ${d.name}` : ''}</div>
            )}
            <div>
              <div className="flex justify-between text-[11px] text-muted mb-1"><span>Sent in the last 24 hours</span><span className="tabular-nums">{d.sent_today} of {d.daily_cap}</span></div>
              <div className="h-1.5 rounded-full bg-slate-800 overflow-hidden"><div className={cn('h-full', pct > 85 ? 'bg-amber-400' : 'bg-emerald-400')} style={{ width: `${pct}%` }} /></div>
              <div className="text-[11px] text-muted mt-1">{d.queued ? `${d.queued} waiting their turn. Messages go out a few seconds apart so the number is not flagged.` : 'Nothing waiting.'}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              {(d.status === 'not_started' || d.status === 'logged_out') && <Button size="sm" onClick={() => act.mutate('start')} loading={act.isPending} data-testid="wa-start">Start and show the code</Button>}
              {d.status === 'disconnected' && <Button size="sm" icon={<RefreshCw size={12} />} onClick={() => act.mutate('start')} loading={act.isPending}>Reconnect</Button>}
              {d.status === 'connected' && <Button size="sm" variant="ghost" icon={<LogOut size={12} />} onClick={() => setConfirmLogout(true)}>Log out this number</Button>}
              {d.status !== 'not_configured' && d.status !== 'connected' && <Button size="sm" variant="ghost" onClick={() => act.mutate('reset')} loading={act.isPending}>New code</Button>}
            </div>
          </div>
          {d.qr_image && d.status !== 'connected' && (
            <div className="flex flex-col items-center gap-2" data-testid="wa-pair-qr">
              <img src={d.qr_image} alt="Scan with WhatsApp on the KAF phone" className="w-52 h-52 rounded-xl bg-white p-2" />
              <div className="text-[11px] text-muted text-center max-w-[13rem]">On the KAF phone: WhatsApp, Settings, Linked devices, Link a device. Scan this.</div>
            </div>
          )}
        </div>
      )}
      <ConfirmDialog open={confirmLogout} title="Log out the KAF WhatsApp number?"
        message="Nothing goes out on WhatsApp until the number is scanned again. People stay connected; their messages wait in the queue."
        confirmLabel="Log out" onCancel={() => setConfirmLogout(false)}
        onConfirm={() => { setConfirmLogout(false); act.mutate('logout'); }} />
    </Card>
  );
}

/** The official project (virtual-cycling-509516-t5), where contact@kaf4media.com lives. */
const GCP = 'virtual-cycling-509516-t5';
const GCP_LINKS = {
  audience: `https://console.cloud.google.com/auth/audience?project=${GCP}`,
  branding: `https://console.cloud.google.com/auth/branding?project=${GCP}`,
  client: `https://console.cloud.google.com/auth/clients/create?project=${GCP}`,
  gmail: `https://console.cloud.google.com/apis/library/gmail.googleapis.com?project=${GCP}`,
  calendar: `https://console.cloud.google.com/apis/library/calendar-json.googleapis.com?project=${GCP}`,
};

function Ext({ href, children }: { href: string; children: React.ReactNode }) {
  return <a href={href} target="_blank" rel="noopener noreferrer" className="text-accent-400 hover:underline">{children}</a>;
}

function GoogleCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['google-connection'], queryFn: fetchGoogle });
  const [params, setParams] = useSearchParams();
  const [cid, setCid] = useState('');
  const [secret, setSecret] = useState('');
  const [editing, setEditing] = useState(false);
  const [confirmOff, setConfirmOff] = useState(false);
  useEffect(() => {
    const g = params.get('google');
    if (!g) return;
    const msg = params.get('msg') ?? '';
    if (g === 'ok') toast.success(msg || 'Google connected'); else toast.error(msg || 'Google was not connected');
    const n = new URLSearchParams(params); n.delete('google'); n.delete('msg'); setParams(n, { replace: true });
    qc.invalidateQueries({ queryKey: ['google-connection'] });
  }, []);   // eslint-disable-line react-hooks/exhaustive-deps
  const saveClient = useMutation({
    mutationFn: () => saveGoogleClient(cid.trim(), secret.trim()),
    onSuccess: (d) => { qc.setQueryData(['google-connection'], d); setCid(''); setSecret(''); setEditing(false); toast.success('Saved. Now press Connect with Google.'); },
    onError: (e) => toast.error(errText(e, 'That client ID does not look right. It ends in .apps.googleusercontent.com')),
  });
  const start = useMutation({ mutationFn: googleStartUrl, onSuccess: (url) => { window.location.href = url; }, onError: (e) => toast.error(errText(e, 'Could not start')) });
  const off = useMutation({ mutationFn: googleDisconnect, onSuccess: (d) => { qc.setQueryData(['google-connection'], d); toast.success('Google disconnected'); } });
  const test = useMutation({ mutationFn: googleTest, onSuccess: (r) => (r.ok ? toast.success(r.message) : toast.error(r.message)) });
  const d = q.data;
  const showSetup = !!d && (!d.client_saved || editing);

  return (
    <Card className="mb-4" data-testid="google-connection">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-accent-600/20 text-accent-400 flex items-center justify-center shrink-0"><Mail size={20} /></div>
        <div className="flex-1">
          <h2 className="font-medium text-muted-strong">Google (mail and calendar)</h2>
          <p className="text-xs text-muted">Warehouse mail goes out as {d?.expected_account ?? 'contact@kaf4media.com'}: email confirmations, script links, and later call invites.</p>
        </div>
        {d?.connected && <span className="text-[11px] rounded-full border border-emerald-500/40 text-emerald-300 px-2 py-0.5">connected</span>}
      </div>
      {!d && <div className="text-sm text-muted flex items-center gap-2"><Loader2 size={14} className="animate-spin" /> Checking...</div>}

      {d?.connected && !editing && (
        <div className="space-y-3" data-testid="google-connected">
          <div className="text-emerald-300 text-lg font-medium">Connected</div>
          <div className="text-sm text-muted-strong">{d.account}</div>
          {d.connected_at && <div className="text-[11px] text-muted">since {new Date(d.connected_at).toLocaleString('en-AE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>}
          {d.last_error && <div className="text-xs text-red-300">{d.last_error.message}</div>}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="secondary" onClick={() => test.mutate()} loading={test.isPending} data-testid="google-test">Send me a test email</Button>
            <Button size="sm" variant="ghost" onClick={() => setConfirmOff(true)}>Disconnect</Button>
          </div>
        </div>
      )}

      {d && d.client_saved && !d.connected && !editing && (
        <div className="space-y-3" data-testid="google-ready">
          {d.last_error && <div className="text-xs text-red-300">{d.last_error.message}</div>}
          <p className="text-sm text-muted-strong">Last step: press the button and sign in as <b>{d.expected_account}</b>. Leave every box ticked on the Google screen.</p>
          <div className="flex flex-wrap gap-2">
            <Button onClick={() => start.mutate()} loading={start.isPending} data-testid="google-connect">Connect with Google</Button>
            <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>Change the client</Button>
          </div>
        </div>
      )}

      {showSetup && d && (
        <div className="space-y-4 text-sm" data-testid="google-setup">
          <ol className="space-y-3 list-decimal pl-5 text-muted-strong marker:text-muted">
            <li>
              Open <Ext href={GCP_LINKS.branding}>Google Auth Platform</Ext> in the official project, signed in as the kaf4media admin.
              If it asks to get started: app name <b>KAF Warehouse</b>, support email <b>{d.expected_account}</b>, then <b>Internal</b>.
              Already set up? Check <Ext href={GCP_LINKS.audience}>Audience</Ext> says <b>Internal</b>.
            </li>
            <li>
              <Ext href={GCP_LINKS.client}>Create a client</Ext>: type <b>Web application</b>, name <b>KAF Warehouse</b>.
              Under <b>Authorised redirect URIs</b> add exactly:
              <div className="mt-1.5 flex items-center gap-2">
                <code className="flex-1 truncate rounded bg-bg border border-slate-800 px-2 py-1 text-xs" data-testid="google-redirect">{d.redirect_uri}</code>
                <Button size="sm" variant="secondary" onClick={() => { navigator.clipboard?.writeText(d.redirect_uri); toast.success('Copied'); }}>Copy</Button>
              </div>
              Press Create. Keep the window with the client ID and secret open.
            </li>
            <li>Turn on <Ext href={GCP_LINKS.gmail}>Gmail API</Ext> and <Ext href={GCP_LINKS.calendar}>Google Calendar API</Ext> (press Enable on each).</li>
            <li>Paste the client ID and secret here. The secret is stored encrypted and never shown again.</li>
          </ol>
          <div className="grid gap-3">
            <Input label="Client ID" placeholder="1234567890-abc...apps.googleusercontent.com" value={cid} onChange={(e) => setCid(e.target.value)} dir="ltr" data-testid="google-client-id" />
            <Input label="Client secret" type="password" autoComplete="off" placeholder="GOCSPX-..." value={secret} onChange={(e) => setSecret(e.target.value)} dir="ltr" data-testid="google-client-secret" />
          </div>
          <div className="flex gap-2">
            <Button onClick={() => saveClient.mutate()} loading={saveClient.isPending} disabled={!cid.trim() || !secret.trim()} data-testid="google-save">Save</Button>
            {editing && <Button variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>}
          </div>
        </div>
      )}
      <ConfirmDialog open={confirmOff} title="Disconnect Google?"
        message="Mail from the Warehouse stops until it is connected again. Access is withdrawn at Google too."
        confirmLabel="Disconnect" onCancel={() => setConfirmOff(false)}
        onConfirm={() => { setConfirmOff(false); off.mutate(); }} />
    </Card>
  );
}

type Filter = 'all' | 'unreachable' | 'waiting';

function ReachabilityBoard() {
  const q = useQuery({ queryKey: ['reachability'], queryFn: fetchReachability });
  const [kind, setKind] = useState<'all' | ReachRow['kind']>('all');
  const [filter, setFilter] = useState<Filter>('all');
  const [text, setText] = useState('');
  const rows = useMemo(() => (q.data ?? []).filter((r) => {
    if (kind !== 'all' && r.kind !== kind) return false;
    const reachable = r.whatsapp.connected || r.telegram.connected || (r.kind !== 'staff' && r.email.connected);
    if (filter === 'unreachable' && reachable) return false;
    if (filter === 'waiting' && !(r.whatsapp.waiting || r.email.waiting)) return false;
    const t = text.trim().toLowerCase();
    return !t || r.name.toLowerCase().includes(t) || (r.clinic ?? '').toLowerCase().includes(t);
  }), [q.data, kind, filter, text]);
  const all = q.data ?? [];
  const team = all.filter((r) => r.kind === 'staff');
  const docs = all.filter((r) => r.kind === 'doctor');
  const count = (rs: ReachRow[], ch: 'telegram' | 'whatsapp' | 'email') => rs.filter((r) => r[ch].connected).length;
  const docsReach = docs.filter((r) => r.whatsapp.connected || r.telegram.connected).length;

  return (
    <Card data-testid="reachability">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
        <div>
          <h2 className="font-medium text-muted-strong">Who can be reached</h2>
          <p className="text-xs text-muted">Everyone the Warehouse messages, and the channels they confirmed.</p>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="reach-totals">
          {([['Team on Telegram', count(team, 'telegram'), team.length], ['Team on WhatsApp', count(team, 'whatsapp'), team.length],
             ['Doctors on WhatsApp', count(docs, 'whatsapp'), docs.length], ['Doctors reachable', docsReach, docs.length]] as const).map(([label, n, of]) => (
            <div key={label} className="rounded-lg border border-slate-800 px-3 py-2">
              <div className="text-lg text-muted-strong tabular-nums">{n}<span className="text-muted text-sm"> of {of}</span></div>
              <div className="text-muted">{label}</div>
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        {(['all', 'staff', 'doctor', 'clinic'] as const).map((k) => (
          <button key={k} onClick={() => setKind(k)} className={cn('text-xs px-2.5 py-1 rounded-full border', kind === k ? 'border-accent-400 text-accent-300 bg-accent-600/15' : 'border-slate-700 text-muted')}>
            {k === 'all' ? 'Everyone' : k === 'staff' ? 'Team' : k === 'doctor' ? 'Doctors' : 'Clinic contacts'}
          </button>
        ))}
        <span className="w-px h-5 bg-slate-800 mx-1" />
        {(['unreachable', 'waiting', 'all'] as const).map((f) => (
          <button key={f} onClick={() => setFilter(f)} className={cn('text-xs px-2.5 py-1 rounded-full border', filter === f ? 'border-accent-400 text-accent-300 bg-accent-600/15' : 'border-slate-700 text-muted')} data-testid={`reach-filter-${f}`}>
            {f === 'unreachable' ? 'Not reachable' : f === 'waiting' ? 'Waiting to confirm' : 'All states'}
          </button>
        ))}
        <div className="relative ml-auto">
          <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
          <input value={text} onChange={(e) => setText(e.target.value)} placeholder="Name or clinic" className="bg-bg border border-slate-700 rounded-lg pl-7 pr-2 py-1.5 text-xs text-muted-strong w-44" />
        </div>
      </div>
      {!q.data && <div className="text-sm text-muted">Loading...</div>}
      {q.data && rows.length === 0 && (
        <div className="flex items-center gap-2 text-sm text-emerald-300 py-6 justify-center"><CheckCircle2 size={16} /> Nobody here. {filter === 'unreachable' ? 'Everyone in this group can be reached.' : ''}</div>
      )}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-[11px] uppercase tracking-wide text-muted text-left">
            <th className="py-2 font-normal">Person</th><th className="font-normal">WhatsApp</th><th className="font-normal">Email</th><th className="font-normal">Telegram</th><th />
          </tr></thead>
          <tbody className="divide-y divide-slate-800/60">
            {rows.map((r) => (
              <tr key={`${r.kind}:${r.client_id}:${r.id}`} data-testid="reach-row">
                <td className="py-2 pr-3">
                  <div className="text-muted-strong">{r.name}</div>
                  <div className="text-[11px] text-muted">{r.kind === 'staff' ? 'Team' : r.kind === 'doctor' ? `Doctor · ${r.clinic}` : `Clinic contact · ${r.clinic}`}</div>
                </td>
                <td className="pr-3"><Pill s={r.whatsapp} /></td>
                <td className="pr-3"><Pill s={r.email} /></td>
                <td className="pr-3"><Pill s={r.telegram} /></td>
                <td className="text-right">
                  {r.kind !== 'staff' && r.client_id && <Link to={`/clients/${r.client_id}`} className="text-[11px] text-accent-400 hover:underline">Onboard</Link>}
                  {r.kind === 'staff' && <Link to={`/messages?person=user:${r.id}`} className="text-[11px] text-accent-400 hover:underline">Messages</Link>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

export default function ConnectionsPage() {
  return (
    <div className="max-w-5xl">
      <PageHeader title="Connections" subtitle="The channels the Warehouse speaks on, and who can be reached." />
      <div className="grid gap-0 lg:grid-cols-2 lg:gap-4">
        <WhatsAppCard />
        <GoogleCard />
      </div>
      <ReachabilityBoard />
    </div>
  );
}
